/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { t } from '@sportninja/common/i18n'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import { NODE_TYPE_KEY } from '@sportninja/common/features/competition/operations/utils/nodeActionLabels'
// Lives in utils.js; TS imports it fine (allowJs). The same helper the legacy
// Setup panel used for exactly this — it unpacks `invalid_fields`, the newer
// `errors` array, and Laravel exception bodies, and strips server traces.
import { getErrorMessage } from '@sportninja/common/utils/utils'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import WarningModal from 'src/components/WarningModal'
import { useDeleteNode } from '../hooks/useDeleteNode'

const translate = t as (key: string, options?: unknown) => string

interface DeleteNodeModalProps {
  node: OperationsNode | null
  rootScheduleId: string
  onClose: () => void
  onDeleted: () => void
}

/**
 * Confirms a destructive delete. A node holding children cannot be deleted —
 * the modal explains why and offers only a dismiss, matching the legacy guard.
 * Deleting a team also destroys the roster linked to that competition, so that
 * consequence is stated explicitly rather than buried.
 */
const DeleteNodeModal = ({
  node,
  rootScheduleId,
  onClose,
  onDeleted,
}: DeleteNodeModalProps) => {
  'use memo'

  const { deleteNode, isDeleting } = useDeleteNode(rootScheduleId)
  const [error, setError] = useState<string | null>(null)

  // This modal never unmounts — it renders null when nothing is targeted — so a
  // failed delete's message would otherwise still be on screen the next time it
  // opens, on a different node.
  useEffect(() => setError(null), [node?.id])

  if (!node) {
    return null
  }

  const hasChildren = node.children.length > 0
  const isTeam = isTeamNode(node)

  // SN-8483 — acknowledge-only. Nothing is deleted here, so a red Confirm
  // beside Cancel offered a choice that did nothing when taken. One primary OK.
  if (hasChildren) {
    return (
      <WarningModal
        isOpen
        hideCancel
        onClose={onClose}
        onConfirm={onClose}
        title={capitalizeWords(translate('common:removeChildrenFirst'))}
        description={translate('common:removeChildrenFirstDescription')}
        confirmLabel={translate('common:ok').toUpperCase()}
      />
    )
  }

  return (
    <WarningModal
      isOpen
      destructive
      isLoading={isDeleting}
      onClose={onClose}
      onConfirm={async () => {
        setError(null)
        try {
          await deleteNode(node)
        } catch (caught) {
          // `mutateAsync` rejects even though the hook's onError toasts, so an
          // unhandled rejection escaped here and the modal closed on nothing.
          // Keep it open with the API's OWN reason inline — Confirm stays live
          // to retry, and delete is the action most likely to be refused for a
          // specific, actionable cause ("Division still has teams").
          setError(getErrorMessage(caught))
          return
        }
        onDeleted()
        onClose()
      }}
      title={capitalizeWords(
        translate('common:removeEntity', {
          entity: translate(NODE_TYPE_KEY[node.type]),
        })
      )}
      // Name the target explicitly. "Remove this team?" gives no way to catch a
      // misclick on the wrong row; "Remove NorthVan Devils?" does.
      description={
        <>
          {isTeam
            ? translate('common:removeNamedTeamDescription', {
                name: node.name,
              })
            : translate('common:removeNamedEntityDescription', {
                name: node.name,
              })}
          {/* The toast is transient and may have faded by the time the user
              looks; this keeps the reason attached to the retry button. */}
          {error && (
            <div css={errorStyle} role='alert'>
              {error}
            </div>
          )}
        </>
      }
      cancelLabel={translate('common:cancel').toUpperCase()}
      confirmLabel={translate('common:confirm').toUpperCase()}
      loadingLabel={translate('common:deleting').toUpperCase()}
    />
  )
}

/* Sits under the confirmation copy, above the divider. Tinted rather than plain
   red text so it reads as a state change on the dark modal ground. */
const errorStyle = css`
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.ERROR_300};
  background: ${colors.ERROR_300}1a;
  color: ${colors.ERROR_100};
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  /* getErrorMessage joins multiple invalid fields with newlines. */
  white-space: pre-line;
`

export default DeleteNodeModal
